import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Table, Alert, Spinner, ButtonGroup, Form } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { createGoogleSpreadsheet, getGoogleSpreadsheetData, getProjectGoogleSheetsInfo } from '../services/api';
import { performParameterInterpolation } from '../services/interpolationApi';
import ParameterImputationModal from './ParameterImputationModal';
import CalculationsModal from './CalculationsModal';
import KpiCalculationModal from './KpiCalculationModal';
import PipelineModal from './PipelineModal';
import MetaboliteColumnsManager from './MetaboliteColumnsManager';
import './ExcelViewer.css';

const ExcelViewer = ({ 
  show, 
  onHide, 
  excelData, 
  filename,
  onAggregateData,
  projectId,
  projectData, // Add project data prop
  onAggregationComplete // Add callback for aggregation completion
}) => {
  const [worksheets, setWorksheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('excel'); // 'excel' or 'google-sheets'
  const [googleSheetsInfo, setGoogleSheetsInfo] = useState(null);
  const [googleSheetsData, setGoogleSheetsData] = useState([]);
  const [isCreatingSheet, setIsCreatingSheet] = useState(false);
  const [autoSync, setAutoSync] = useState(false);
  const [syncInterval, setSyncInterval] = useState(null);  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [isSyncingToExcel, setIsSyncingToExcel] = useState(false);    // Parameter Imputation Modal state
  const [showImputationModal, setShowImputationModal] = useState(false);
  const [showKpiModal, setShowKpiModal] = useState(false);
  const [interpolatedCells, setInterpolatedCells] = useState(new Set()); // Track interpolated cell positions
  const [lastInterpolationResult, setLastInterpolationResult] = useState(null);
  
  // Calculations Modal state
  const [showCalculationsModal, setShowCalculationsModal] = useState(false);
  
  // Pipeline Modal state
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  
  // Enhanced Table Features State
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [sortedData, setSortedData] = useState([]);
  
  // Define auto-sync functions first
  const loadGoogleSheetsData = useCallback(async (spreadsheetId = null) => {
    try {
      setLoading(true);
      let id = spreadsheetId;
      
      // If no specific ID provided, try to get from current state or fetch latest
      if (!id) {
        if (googleSheetsInfo?.spreadsheet_id) {
          id = googleSheetsInfo.spreadsheet_id;
        } else if (projectId) {
          // Fetch the latest Google Sheets info from the project
          try {
            console.log('loadGoogleSheetsData: Fetching latest Google Sheets info...');
            const response = await getProjectGoogleSheetsInfo(projectId);
            if (response.status === 'success' && response.googleSheetInfo) {
              id = response.googleSheetInfo.spreadsheet_id;
              setGoogleSheetsInfo(response.googleSheetInfo);
              console.log('loadGoogleSheetsData: Updated Google Sheets info:', response.googleSheetInfo);
            }
          } catch (fetchError) {
            console.warn('loadGoogleSheetsData: Could not fetch project Google Sheets info:', fetchError);
          }
        }
      }
      
      if (!id) {
        throw new Error('No Google Sheet ID available');
      }

      console.log('loadGoogleSheetsData: Loading data from sheet:', id);
      const response = await getGoogleSpreadsheetData(id);
      setGoogleSheetsData(response.data || []);
      setLastSyncTime(new Date());
    } catch (error) {
      console.error('Error loading Google Sheets data:', error);
      setError(`Failed to load Google Sheets data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [googleSheetsInfo, projectId]);

  const startAutoSync = useCallback(() => {
    if (syncInterval) return;
    
    const interval = setInterval(() => {
      loadGoogleSheetsData();
    }, 30000); // Sync every 30 seconds
    
    setSyncInterval(interval);
  }, [syncInterval, loadGoogleSheetsData]);

  const stopAutoSync = useCallback(() => {
    if (syncInterval) {
      clearInterval(syncInterval);
      setSyncInterval(null);
    }
  }, [syncInterval]);

  // Enhanced Table Utility Functions
  const isNumeric = (value) => {
    if (value === null || value === undefined || value === '') return false;
    return !isNaN(parseFloat(value)) && isFinite(value);
  };

  const detectOutliers = (values, threshold = 2) => {
    const numericValues = values.filter(isNumeric).map(Number);
    if (numericValues.length < 3) return new Set();
    
    const mean = numericValues.reduce((sum, val) => sum + val, 0) / numericValues.length;
    const stdDev = Math.sqrt(numericValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / numericValues.length);
    
    const outliers = new Set();
    values.forEach((value, index) => {
      if (isNumeric(value)) {
        const zScore = Math.abs((Number(value) - mean) / stdDev);
        if (zScore > threshold) {
          outliers.add(index);
        }
      }
    });
    return outliers;
  };

  const getHeatmapColor = (value, min, max) => {
    if (!isNumeric(value)) return '';
    const num = Number(value);
    const ratio = (num - min) / (max - min);
    
    if (ratio < 0.33) return 'heatmap-low';
    if (ratio < 0.67) return 'heatmap-medium';
    return 'heatmap-high';
  };

  const sortData = useCallback((data, key, direction) => {
    if (!data || !key) return data;
    
    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      
      // Handle numeric values
      if (isNumeric(aVal) && isNumeric(bVal)) {
        aVal = Number(aVal);
        bVal = Number(bVal);
      } else {
        // Handle string values
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, []);

  const handleSort = useCallback((columnIndex) => {
    if (!worksheets.length) return;
    
    const currentSheet = worksheets[activeSheet];
    if (!currentSheet || !currentSheet.data.length) return;
    
    let direction = 'asc';
    if (sortConfig.key === columnIndex && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key: columnIndex, direction });
    
    // Sort the data (excluding header row)
    const dataRows = currentSheet.data.slice(1);
    const sorted = sortData(dataRows, columnIndex, direction);
    setSortedData(sorted);
  }, [worksheets, activeSheet, sortConfig, sortData]);

  const parseExcelData = useCallback(() => {
    try {
      setLoading(true);
      setError('');

      // Parse the workbook
      const workbook = XLSX.read(excelData, { type: 'array' });
      const sheets = [];
      
      workbook.SheetNames.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1,
          defval: '',
          raw: false 
        });
        
        sheets.push({
          name: sheetName,
          data: jsonData
        });
      });

      setWorksheets(sheets);
      setActiveSheet(0);
    } catch (err) {
      console.error('Error parsing Excel data:', err);
      setError('Failed to parse Excel file. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [excelData]);

  const checkForExistingGoogleSheet = useCallback(async () => {
    try {
      const response = await getProjectGoogleSheetsInfo(projectId);
      if (response.status === 'success' && response.googleSheetInfo) {
        setGoogleSheetsInfo(response.googleSheetInfo);
      }
    } catch (error) {
      console.log('No existing Google Sheet found for this project');
    }
  }, [projectId]);

  useEffect(() => {
    if (excelData && show) {
      parseExcelData();
      if (projectId) {
        checkForExistingGoogleSheet();
      }
    }
    
    // Cleanup when modal is hidden
    if (!show) {
      stopAutoSync();
      setError('');
      setLoading(false);
    }
  }, [excelData, show, projectId, parseExcelData, checkForExistingGoogleSheet, stopAutoSync]);

  useEffect(() => {
    if (autoSync && googleSheetsInfo && viewMode === 'google-sheets') {
      startAutoSync();
    } else {
      stopAutoSync();
    }
    
    return () => stopAutoSync();
  }, [autoSync, googleSheetsInfo, viewMode, startAutoSync, stopAutoSync]);

  // Cleanup effect when component unmounts or modal is hidden
  useEffect(() => {
    return () => {
      stopAutoSync();
    };  }, [stopAutoSync]);

  // Reset sorting when worksheets or active sheet changes
  useEffect(() => {
    setSortConfig({ key: null, direction: 'asc' });
    setSortedData([]);
  }, [worksheets, activeSheet]);

  const createGoogleSheet = async () => {
    try {
      setIsCreatingSheet(true);
      setError('');

      console.log('ExcelViewer: Creating Google Sheet with projectId:', projectId);
      console.log('ExcelViewer: Props received:', { projectId, filename, worksheets: worksheets.length });

      if (!worksheets.length) {
        throw new Error('No Excel data available to create Google Sheet');
      }

      if (!projectId) {
        throw new Error('Project ID is required to create Google Sheet');
      }

      const title = `${filename || 'Bioreactor Data'} - ${new Date().toLocaleDateString()}`;
      const excelDataArray = worksheets[0].data; // Use first worksheet

      console.log('ExcelViewer: Calling createGoogleSpreadsheet with:', { title, projectId, dataLength: excelDataArray.length });
      const response = await createGoogleSpreadsheet(title, excelDataArray, projectId);
      
      setGoogleSheetsInfo({
        spreadsheet_id: response.spreadsheet_id,
        spreadsheet_url: response.spreadsheet_url,
        title: response.title,
        created_at: new Date().toISOString()
      });

      // Switch to Google Sheets view
      setViewMode('google-sheets');
      
      // Load the Google Sheets data
      await loadGoogleSheetsData(response.spreadsheet_id);

    } catch (error) {
      console.error('Error creating Google Sheet:', error);
      // Better error message handling
      let errorMessage = 'Unknown error occurred';
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.detail) {
        errorMessage = error.detail;
      } else {
        errorMessage = JSON.stringify(error);
      }
      setError(`Failed to create Google Sheet: ${errorMessage}`);
    } finally {
      setIsCreatingSheet(false);
    }  };
  const syncFromGoogleSheets = useCallback(async () => {
    // Get the latest Google Sheets info first
    let currentGoogleSheetsInfo = googleSheetsInfo;
    
    // If we have a projectId, fetch the latest Google Sheets info to ensure we're syncing from the correct sheet
    if (projectId && (!currentGoogleSheetsInfo || !currentGoogleSheetsInfo.spreadsheet_id)) {
      try {
        console.log('syncFromGoogleSheets: Fetching latest Google Sheets info...');
        const response = await getProjectGoogleSheetsInfo(projectId);
        if (response.status === 'success' && response.googleSheetInfo) {
          currentGoogleSheetsInfo = response.googleSheetInfo;
          setGoogleSheetsInfo(currentGoogleSheetsInfo);
          console.log('syncFromGoogleSheets: Updated Google Sheets info:', currentGoogleSheetsInfo);
        }
      } catch (error) {
        console.warn('syncFromGoogleSheets: Could not fetch latest Google Sheets info:', error);
      }
    }

    if (!currentGoogleSheetsInfo || !currentGoogleSheetsInfo.spreadsheet_id) {
      setError('No Google Sheet available to sync from');
      return;
    }

    try {
      setIsSyncingToExcel(true);
      setError('');

      console.log('syncFromGoogleSheets: Syncing from sheet:', currentGoogleSheetsInfo.spreadsheet_id);

      // Load latest data from Google Sheets
      const response = await getGoogleSpreadsheetData(currentGoogleSheetsInfo.spreadsheet_id);
      const googleData = response.data || [];

      if (googleData.length === 0) {
        setError('No data found in Google Sheets');
        return;
      }

      // Convert Google Sheets data back to Excel format
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(googleData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Synced Data');

      // Update the worksheets state with synced data
      setWorksheets([{
        name: 'Synced Data',
        data: googleData
      }]);
      setActiveSheet(0);
      setLastSyncTime(new Date());
      
      // Show success message briefly
      const successMsg = 'Successfully synced data from Google Sheets!';
      setError(''); // Clear any existing errors
      
      // Optional: You could add a success state if you want to show a success message
      console.log(successMsg);
      
    } catch (error) {
      console.error('Error syncing from Google Sheets:', error);
      setError(`Failed to sync from Google Sheets: ${error.message}`);
    } finally {
      setIsSyncingToExcel(false);
    }
  }, [googleSheetsInfo, projectId]);// Add function to refresh Google Sheets data after aggregation
  const refreshAfterAggregation = useCallback(async (aggregationResult) => {
    console.log('ExcelViewer: Refreshing after aggregation completion:', aggregationResult);
    
    try {
      // Validate aggregation result
      if (!aggregationResult) {
        console.warn('ExcelViewer: No aggregation result provided to refresh function');
        return;
      }

      // IMPORTANT: Refresh the Google Sheets info from the database 
      // because the aggregation process updates the sheet in the projects table
      if (projectId) {
        console.log('ExcelViewer: Fetching latest Google Sheets info after aggregation...');
        
        try {          // Fetch the latest Google Sheets info from the project
          const response = await getProjectGoogleSheetsInfo(projectId);
          if (response.status === 'success' && response.googleSheetInfo) {
            console.log('ExcelViewer: Updated Google Sheets info:', response.googleSheetInfo);
            
            // Update our local state with the latest Google Sheets info
            setGoogleSheetsInfo(response.googleSheetInfo);
            
            // Now reload the data from the correct (updated) Google Sheet
            if (response.googleSheetInfo.spreadsheet_id) {
              console.log('ExcelViewer: Reloading data from updated Google Sheet...');
              setError(''); // Clear any existing errors
              
              // Load data from the correct Google Sheet (the one that was actually updated)
              await loadGoogleSheetsData(response.googleSheetInfo.spreadsheet_id);
              
              // If we're currently in Google Sheets view, show success
              if (viewMode === 'google-sheets') {
                console.log('ExcelViewer: Google Sheets data refreshed successfully');
              }
              
              // If we're in Excel view but have Google Sheets integration, sync from the updated sheet
              if (viewMode === 'excel') {
                console.log('ExcelViewer: Auto-syncing from updated Google Sheets to Excel view...');
                await syncFromGoogleSheets();
              }
            }
          } else {
            console.warn('ExcelViewer: No Google Sheets info found in project after aggregation');
          }
        } catch (fetchError) {
          console.error('ExcelViewer: Error fetching updated Google Sheets info:', fetchError);
          setError(`Failed to fetch updated Google Sheets info: ${fetchError.message}`);
        }
      }
      
      // Call the parent aggregation complete handler if provided
      if (onAggregationComplete) {
        onAggregationComplete(aggregationResult);
      }
      
    } catch (error) {
      console.error('ExcelViewer: Error refreshing after aggregation:', error);
      setError(`Failed to refresh data after aggregation: ${error.message}`);
    }
  }, [projectId, viewMode, onAggregationComplete, loadGoogleSheetsData, syncFromGoogleSheets]);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setError('');
    
    if (mode === 'google-sheets') {
      setIframeLoading(true); // Reset loading state when switching to Google Sheets
      if (googleSheetsInfo) {
        loadGoogleSheetsData();
      } else {
        // No existing Google Sheet - will show creation option
        setGoogleSheetsData([]);
        setIframeLoading(false);
      }
    }
  };

  const renderExcelTable = () => {
    if (!worksheets.length) return null;

    const currentSheet = worksheets[activeSheet];
    if (!currentSheet || !currentSheet.data.length) return null;

    const headers = currentSheet.data[0] || [];
    const rows = sortedData.length ? sortedData : currentSheet.data.slice(1);

    // Calculate heatmap ranges for numeric columns
    const heatmapRanges = {};
    const outlierSets = {};
    
    headers.forEach((header, colIndex) => {
      const columnValues = rows.map(row => row[colIndex]).filter(val => val !== null && val !== undefined);
      const numericValues = columnValues.filter(isNumeric).map(Number);
      
      if (numericValues.length > 0) {
        heatmapRanges[colIndex] = {
          min: Math.min(...numericValues),
          max: Math.max(...numericValues)
        };
        outlierSets[colIndex] = detectOutliers(columnValues);
      }
    });

    return (
      <div className="excel-table-container enhanced-table">
        <div className="table-responsive">
          <table className="table table-sm enhanced-data-table">
            <thead className="table-header-sticky">
              <tr>
                {headers.map((header, index) => (
                  <th 
                    key={index} 
                    className={`excel-header sortable-header ${index === 0 ? 'sticky-column' : ''} ${
                      sortConfig.key === index ? `sorted-${sortConfig.direction}` : ''
                    }`}
                    onClick={() => handleSort(index)}
                  >
                    <div className="header-content">
                      <span className="header-text">{header || `Column ${index + 1}`}</span>
                      <div className="sort-indicators">
                        <span className={`sort-arrow sort-asc ${
                          sortConfig.key === index && sortConfig.direction === 'asc' ? 'active' : ''
                        }`}>▲</span>
                        <span className={`sort-arrow sort-desc ${
                          sortConfig.key === index && sortConfig.direction === 'desc' ? 'active' : ''
                        }`}>▼</span>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="enhanced-table-row">
                  {headers.map((_, colIndex) => {
                    const cellValue = row[colIndex] || '';
                    const isOutlier = outlierSets[colIndex] && outlierSets[colIndex].has(rowIndex);
                    const heatmapClass = heatmapRanges[colIndex] && isNumeric(cellValue) 
                      ? getHeatmapColor(cellValue, heatmapRanges[colIndex].min, heatmapRanges[colIndex].max)
                      : '';
                    
                    return (
                      <td 
                        key={colIndex} 
                        className={`excel-cell enhanced-cell ${colIndex === 0 ? 'sticky-column' : ''} ${heatmapClass}`}
                      >
                        <div className="cell-content">
                          <span className="cell-value">{cellValue}</span>
                          {isOutlier && (
                            <span className="outlier-indicator" title="Potential outlier value">
                              ⚠️
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderGoogleSheetsView = () => {
    if (!googleSheetsInfo) {
      return (
        <div className="google-sheets-setup">
          <Alert variant="info">
            <h5>Create Google Sheets Version</h5>
            <p>Convert your Excel data to Google Sheets for collaborative editing and real-time synchronization.</p>
            <Button 
              variant="success" 
              onClick={createGoogleSheet}
              disabled={isCreatingSheet || !worksheets.length}
            >
              {isCreatingSheet ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                '📊 Create Google Sheet'
              )}
            </Button>
          </Alert>
        </div>
      );
    }

    return (
      <div className="google-sheets-viewer">
        <div className="google-sheets-controls">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 className="mb-1">{googleSheetsInfo.title}</h6>
              <small className="text-muted">
                Created: {new Date(googleSheetsInfo.created_at).toLocaleString()}
              </small>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Form.Check
                type="switch"
                id="auto-sync-switch"
                label="Auto-sync"
                checked={autoSync}
                onChange={(e) => setAutoSync(e.target.checked)}
                className="d-flex align-items-center"
              />
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => loadGoogleSheetsData()}
                disabled={loading}
                className="d-flex align-items-center gap-1"
              >
                {loading ? <Spinner animation="border" size="sm" /> : <i className="ti ti-refresh"></i>}
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Metabolite Columns Manager */}
        <MetaboliteColumnsManager 
          projectId={projectId}
          googleSheetsInfo={googleSheetsInfo}
          onColumnsAdded={(result) => {
            // Refresh Google Sheets data after columns are added
            loadGoogleSheetsData();
            
            // Show success message
            console.log('Metabolite columns added successfully:', result);
          }}
          className="mb-3"
        />

        <div className="google-sheets-iframe-container">
          <div className="iframe-wrapper">
            <iframe
              src={`${googleSheetsInfo.spreadsheet_url}#gid=0`}
              className="google-sheets-iframe"
              title="Google Sheets Editor"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              onLoad={() => setIframeLoading(false)}
              onError={() => {
                setIframeLoading(false);
                setError('Failed to load Google Sheets. Please try opening it in a new tab.');
              }}
            />
            {iframeLoading && (
              <div className="iframe-loading-overlay">
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading Google Sheets...</span>
                  </div>
                  <div className="ms-3">
                    <h6 className="mb-1">Loading Google Sheets</h6>
                    <small className="text-muted">This may take a few moments...</small>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="iframe-controls">
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                <i className="ti ti-external-link me-1"></i>
                Google Sheets Embedded View
                {!iframeLoading && (
                  <span className="text-success ms-2">
                    <i className="ti ti-check-circle"></i> Loaded
                  </span>
                )}
              </small>
              <div className="iframe-actions">
                <a 
                  href={googleSheetsInfo.spreadsheet_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary"
                  title="Open in new tab for full functionality"
                >
                  <i className="ti ti-external-link me-1"></i>
                  Open in Google Sheets
                </a>
              </div>
            </div>
          </div>
        </div>        <div className="google-sheets-data-preview mt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">Data Preview (Read-only sync)</h6>
            {interpolatedCells.size > 0 && (
              <div className="interpolation-legend">
                <small className="text-muted d-flex align-items-center">
                  <span className="interpolated-cell-indicator me-1"></span>
                  <span>✨ = Interpolated values</span>
                </small>
              </div>
            )}
          </div>
          {googleSheetsData.length > 0 ? (
            <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    {googleSheetsData[0]?.map((header, index) => (
                      <th key={index} className="excel-header">
                        {header || `Column ${index + 1}`}
                      </th>
                    ))}
                  </tr>
                </thead>                <tbody>
                  {googleSheetsData.slice(1).map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {googleSheetsData[0]?.map((_, colIndex) => {
                        const cellKey = `${rowIndex + 1}-${colIndex}`;
                        const isInterpolated = interpolatedCells.has(cellKey);
                        return (
                          <td 
                            key={colIndex} 
                            className={`excel-cell ${isInterpolated ? 'interpolated-cell' : ''}`}
                            title={isInterpolated ? 'This value was interpolated' : ''}
                          >
                            {row[colIndex] || ''}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Alert variant="info" className="mb-0">
              No data synchronized yet. Make changes in the Google Sheet above.
            </Alert>
          )}
        </div>
      </div>
    );
  };  const handleAggregateClick = () => {
    if (onAggregateData) {
      const dataToAggregate = viewMode === 'google-sheets' && googleSheetsData.length > 0 
        ? [{ name: 'Google Sheet Data', data: googleSheetsData }]
        : worksheets;
      // Pass the refresh callback to the DataMapper
      onAggregateData(dataToAggregate, filename, refreshAfterAggregation);
    }
  };  const handleParametersImputationClick = () => {
    // Open the Parameter Imputation Modal
    setShowImputationModal(true);
  };
  
  const handleCalculateKpiClick = () => {
    // Open the Calculate KPI Modal
    setShowKpiModal(true);
  };
  
  const handleCalculationsClick = () => {
    // Open the Calculations Modal
    setShowCalculationsModal(true);
  };

  const handlePipelineClick = () => {
    // Open the Pipeline Modal
    console.log('Pipeline button clicked - opening modal');
    setShowPipelineModal(true);
  };
  
  const handleApplyCalculations = (calculationResults, selectedCalculations) => {
    // Handle the calculation results
    console.log('Applied calculations:', calculationResults);
    console.log('Selected calculations:', selectedCalculations);
    
    // In a real implementation, you would:
    // 1. Apply the calculations to the current data
    // 2. Update the display with calculated values
    // 3. Optionally save the results to the database
    
    // For now, just close the modal
    setShowCalculationsModal(false);
    
    // Show a success message
    alert(`Successfully applied ${selectedCalculations.size} calculations to the data!`);
  };const handleStartInterpolation = async (mappings, columns) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Starting interpolation with mappings:', mappings);
      console.log('Available columns:', columns);
      
      // Filter out empty mappings
      const validMappings = Object.fromEntries(
        Object.entries(mappings).filter(([key, value]) => value && value.trim() !== '')
      );
      
      if (Object.keys(validMappings).length === 0) {
        setError('Please select at least one interpolation method for the columns.');
        setLoading(false);
        return;
      }
      
      // Call the backend API
      const result = await performParameterInterpolation(projectId, {
        mappings: validMappings,
        availableColumns: columns
      });
      
      if (result.success) {
        // Store the interpolation result for tracking
        setLastInterpolationResult(result);
        
        // Show success message with details
        const interpolatedCount = Object.values(result.interpolatedCount || {}).reduce((sum, count) => sum + count, 0);
        setError(''); // Clear any previous errors
        
        // Refresh the data to show interpolated values
        if (viewMode === 'google-sheets' && googleSheetsInfo) {
          await loadGoogleSheetsData();
          
          // Track interpolated cells for visual highlighting
          // Note: In a full implementation, the backend should return cell positions
          // For now, we'll mark all cells in interpolated columns as potentially interpolated
          const newInterpolatedCells = new Set();
          if (result.interpolatedColumns && googleSheetsData.length > 1) {
            result.interpolatedColumns.forEach(columnName => {
              const columnIndex = googleSheetsData[0]?.indexOf(columnName);
              if (columnIndex >= 0) {
                // Mark all data rows for this column (skip header row)
                for (let rowIndex = 1; rowIndex < googleSheetsData.length; rowIndex++) {
                  newInterpolatedCells.add(`${rowIndex}-${columnIndex}`);
                }
              }
            });
          }
          setInterpolatedCells(newInterpolatedCells);
        }
        
        // Show success feedback with detailed information
        const message = `✅ Interpolation completed successfully!\n\n` +
          `📊 Summary:\n` +
          `• ${interpolatedCount} missing values filled\n` +
          `• ${result.interpolatedColumns?.length || 0} columns processed\n` +
          `• Methods used: ${Object.values(validMappings).join(', ')}\n\n` +
          `The data has been updated in your Google Sheet with highlighted interpolated values.`;
        
        alert(message);
        
        console.log('Interpolation completed:', result);
        
        // Close the modal on success
        setShowImputationModal(false);
      } else {
        setError(result.message || 'Interpolation failed. Please try again.');
      }
      
    } catch (error) {
      console.error('Error during interpolation:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'An error occurred during interpolation. Please check your data and try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Modal 
        show={show} 
        onHide={onHide} 
        size="xl" 
        centered
        className="excel-viewer-modal"
      >
      <Modal.Header closeButton className="excel-viewer-header">
        <div className="excel-viewer-title-section">
          <Modal.Title>Data Viewer</Modal.Title>
          <div className="excel-filename">{filename}</div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <ButtonGroup size="sm">
            <Button 
              variant={viewMode === 'excel' ? 'primary' : 'outline-primary'}
              onClick={() => handleViewModeChange('excel')}
            >
              📄 Excel View
            </Button>            <Button 
              variant={viewMode === 'google-sheets' ? 'primary' : 'outline-primary'}
              onClick={() => handleViewModeChange('google-sheets')}
              disabled={!projectId}
            >
              📊 Google Sheets
            </Button>
          </ButtonGroup>          <Button 
            variant="success" 
            className="aggregate-button"
            onClick={handleAggregateClick}
            disabled={loading || (!worksheets.length && !googleSheetsData.length)}
          >
            📊 Aggregate Data
          </Button>

          <Button 
            variant="warning" 
            className="calculations-button"
            onClick={handleCalculationsClick}
            disabled={loading || (!worksheets.length && !googleSheetsData.length)}
          >
            🧮 Calculations
          </Button>

          <Button 
            variant="info" 
            className="parameters-imputation-button"
            onClick={handleParametersImputationClick}
            disabled={loading || (!worksheets.length && !googleSheetsData.length)}
          >
            🔧 Parameters Imputation
          </Button>

          <Button 
            variant="secondary" 
            className="pipeline-button"
            onClick={handlePipelineClick}
            disabled={loading || (!worksheets.length && !googleSheetsData.length)}
          >
            🚀 Pipeline
          </Button>

          {/* Hide Calculate KPI button */}
          {/* 
          <Button 
            variant="primary" 
            className="calculate-kpi-button"
            onClick={handleCalculateKpiClick}
            disabled={loading || (!worksheets.length && !googleSheetsData.length)}
          >
            📈 Calculate KPI
          </Button>
          */}
        </div>
      </Modal.Header>
      
      <Modal.Body className="excel-viewer-body">
        {loading && (
          <div className="text-center p-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <div className="mt-2">
              {viewMode === 'google-sheets' ? 'Syncing with Google Sheets...' : 'Parsing Excel file...'}
            </div>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>            {viewMode === 'excel' && (
              <>
                {/* Sync controls for Excel view when Google Sheets is available */}
                {googleSheetsInfo && (
                  <div className="excel-sync-controls mb-3 p-3 bg-light rounded">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="mb-1">📊 Google Sheets Integration</h6>
                        <small className="text-muted">
                          Sync with: {googleSheetsInfo.title}
                          {lastSyncTime && (
                            <span className="ms-2">
                              | Last sync: {lastSyncTime.toLocaleString()}
                            </span>
                          )}
                        </small>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={syncFromGoogleSheets}
                          disabled={isSyncingToExcel || loading}
                        >
                          {isSyncingToExcel ? (
                            <>
                              <Spinner as="span" animation="border" size="sm" className="me-1" />
                              Syncing...
                            </>
                          ) : (
                            <>🔄 Sync from Google Sheets</>
                          )}
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => window.open(googleSheetsInfo.spreadsheet_url, '_blank')}
                        >
                          🔗 Open in Google Sheets
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {worksheets.length > 1 && (
                  <div className="sheet-tabs mb-3">
                    {worksheets.map((sheet, index) => (
                      <Button
                        key={index}
                        variant={index === activeSheet ? "primary" : "outline-secondary"}
                        size="sm"
                        onClick={() => setActiveSheet(index)}
                        className="me-2"
                      >
                        {sheet.name}
                      </Button>
                    ))}
                  </div>
                )}

                {worksheets.length > 0 && (
                  <div className="excel-stats mb-3">
                    <small className="text-muted">
                      Sheet: {worksheets[activeSheet]?.name} | 
                      Rows: {worksheets[activeSheet]?.data.length - 1} | 
                      Columns: {worksheets[activeSheet]?.data[0]?.length || 0}
                    </small>
                  </div>
                )}

                {renderExcelTable()}

                {worksheets.length === 0 && (
                  <Alert variant="info">
                    No data found in the Excel file.
                  </Alert>
                )}
              </>
            )}

            {viewMode === 'google-sheets' && renderGoogleSheetsView()}
          </>
        )}
      </Modal.Body>
        <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>      </Modal>      {/* Parameter Imputation Modal */}
      <ParameterImputationModal
        show={showImputationModal}
        onHide={() => setShowImputationModal(false)}
        googleSheetsData={googleSheetsData}
        googleSheetsInfo={googleSheetsInfo}
        projectData={projectData}
        onStartInterpolation={handleStartInterpolation}
        loading={loading}
        error={error}
      />      {/* Calculations Modal */}
      <CalculationsModal
        show={showCalculationsModal}
        onHide={() => setShowCalculationsModal(false)}
        excelData={viewMode === 'google-sheets' ? googleSheetsData : worksheets}
        onApplyCalculations={handleApplyCalculations}
        projectId={projectId}
      />

      {/* KPI Calculation Modal */}
      <KpiCalculationModal
        show={showKpiModal}
        onHide={() => setShowKpiModal(false)}
        worksheets={viewMode === 'google-sheets' ? [{ name: 'Google Sheets Data', data: googleSheetsData }] : worksheets}
        projectId={projectId}
      />

      {/* Pipeline Modal */}
      <PipelineModal
        show={showPipelineModal}
        onHide={() => setShowPipelineModal(false)}
        projectId={projectId}
        projectData={projectData}
        excelData={viewMode === 'excel' ? worksheets : null}
        googleSheetsData={viewMode === 'google-sheets' ? googleSheetsData : null}
        googleSheetsInfo={googleSheetsInfo}
      />
    </>
  );
};

export default ExcelViewer;
